// ─────────────────────────────────────────────────────────────────────────────
// ARPFlow Campaign Live Engine
//
// Companion to dataEngine.js, which reads the invoice_funnel_metrics snapshot
// (stale by up to a day, all-zero until the ETL runs). This one consumes
// GET /api/campaign/live-metrics — live ticket state from the scoreboard
// service — so the numbers match the action-page KPI cards.
//
// Three campaigns ship: Founder Summary, Founder WoW and Responsibility
// Message. Finance Exec and Ops Exec are retired. Every one of them exports as
// a SheetWA variant workbook — one sheet per message shape — because SheetWA
// applies a single template per sheet.
//
// dataEngine.js itself is left untouched; the legacy dashboard still uses it.
// ─────────────────────────────────────────────────────────────────────────────

import * as XLSX from 'xlsx';

const YT_LINK = 'https://youtu.be/4u6R11lTEm4?si=216wm9f6aixVhEk3';

export const ISSUE_TYPES = {
  payment_overdue:  { key: 'payment_overdue',  label: 'Overdue',     short: 'Overdue' },
  unbooked_invoice: { key: 'unbooked_invoice', label: 'Rejected',    short: 'Rejected' },
  grn_short:        { key: 'grn_short',        label: 'Discrepancy', short: 'Short GRN' },
  grn_missing:      { key: 'grn_missing',      label: 'No GRN',      short: 'No GRN' },
};

export const ISSUE_ORDER = ['payment_overdue', 'unbooked_invoice', 'grn_missing', 'grn_short'];

// The Finance Exec and Ops Exec campaigns are gone, but these sets still define
// ALL_DESIGS — the audience every remaining campaign sends to — and back the
// DESIGNATIONS export, so they are not dead.
const FOUNDER_DESIGS = new Set(['FOUNDER_CEO', 'FOUNDER_CFO', 'FOUNDER_COO']);
const FIN_DESIGS     = new Set(['FINANCE_CONTROLLER', 'FINANCE_EXECUTIVE']);
const OPS_DESIGS     = new Set(['OPS_EXECUTIVE', 'OPERATIONS_HEAD']);
const ALL_DESIGS     = new Set([...FOUNDER_DESIGS, ...FIN_DESIGS, ...OPS_DESIGS]);

export const DESIGNATIONS = { FOUNDER: FOUNDER_DESIGS, FINANCE: FIN_DESIGS, OPS: OPS_DESIGS };

// ── Formatting (identical to dataEngine.js) ──────────────────────────────────

export function fmtInr(val) {
  const n = Math.abs(parseFloat(val) || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1).replace('.0', '')}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1).replace('.0', '')}L`;
  if (n >= 1000) return `₹${Math.round(n / 1000)}K`;
  return `₹${Math.round(n)}`;
}

// null means "no prior-week baseline" — genuinely not comparable, so it must
// render as N/A and never as a 0% badge.
export function fmtPctStr(pct) {
  if (pct === null || pct === undefined) return 'N/A';
  const n = parseFloat(pct);
  if (Number.isNaN(n)) return 'N/A';
  if (n > 0) return `+${Math.round(n)}%`;
  if (n < 0) return `${Math.round(n)}%`;
  return '0%';
}

export function fmtPctDir(pct) {
  if (pct === null || pct === undefined) return 'N/A';
  const n = parseFloat(pct);
  if (Number.isNaN(n)) return 'N/A';
  if (n > 0) return `up ${Math.round(Math.abs(n))}%`;
  if (n < 0) return `down ${Math.round(Math.abs(n))}%`;
  return 'flat (0%)';
}

export const deltaDirection = pct => {
  if (pct === null || pct === undefined) return 'na';
  const n = parseFloat(pct);
  if (Number.isNaN(n)) return 'na';
  return n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
};

export const ALLOCATION_LABEL = {
  FULL:    { label: 'Allocated',        tone: 'green' },
  PARTIAL: { label: 'Partly allocated', tone: 'amber' },
  NONE:    { label: 'Not allocated',    tone: 'red' },
};

// ── Shaping ──────────────────────────────────────────────────────────────────

const EMPTY_ISSUE = {
  currentValue: 0, currentCount: 0, pendingValue: 0, disputeValue: 0,
  old90Value: 0, over30Value: 0, over30Count: 0, over30LastWeekValue: 0,
  over30DeltaPct: null, assignedValue: 0, unassignedValue: 0,
  lastWeekValue: 0, deltaValue: 0, deltaPct: null, closedLastWeekValue: 0,
  lastWeekComparable: false,
};

export const ACTIVATION_LABEL = {
  ACTIVE: { label: 'Active', tone: 'green' },
  TRIAL:  { label: 'Trial',  tone: 'amber' },
};

export const isTrial = c => c?.activationStatus === 'TRIAL';

// A client's week-on-week figures are only meaningful when the backend says the
// baseline covers the same platforms the current number is built from.
export const isWowComparable = c => Boolean(c?.wowComparable);

// Human-readable reason a client's WoW is suppressed, for tooltips.
export function wowBlockedReason(client) {
  if (isWowComparable(client)) return null;
  const added = client?.newPlatforms || [];
  if (added.length) {
    const names = added.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
    return `${names} went live since the comparison point, so this week is not like-for-like.`;
  }
  return 'No comparable baseline for this client yet.';
}

const issue = (client, key) => client?.metrics?.byIssueType?.[key] ?? EMPTY_ISSUE;
export const issueMetric = issue;

// Adapts a live client into the metric shape dataEngine.js's message builders
// expect, so those builders could be ported verbatim rather than rewritten.
//
// Two mappings worth knowing:
//   noGrn30*  ← grn_missing.over30* (the 30+ subset, matching the legacy
//               no_grn_gt30d_* columns — NOT the all-ages grn_missing total)
//   discAddedThisWeek ← the week-on-week rise in grn_short, floored at 0
export function toLegacyMetrics(client) {
  const ov = issue(client, 'payment_overdue');
  const rej = issue(client, 'unbooked_invoice');
  const grn = issue(client, 'grn_missing');
  const sg = issue(client, 'grn_short');
  // deltaValue is a raw current-minus-baseline subtraction the backend computes
  // regardless of comparability. Quoting it when the platform sets differ would
  // report a platform going live as "new discrepancies raised this week", so it
  // is only trusted when the comparison is like-for-like.
  const comparable = isWowComparable(client);
  const discAdded = comparable ? Math.max(0, sg.deltaValue || 0) : 0;

  return {
    clientId: client.clientId,
    clientName: client.clientName,
    activationStatus: client.activationStatus ?? null,
    newPlatform: Boolean(client.newPlatform),
    newPlatforms: client.newPlatforms || [],
    wowComparable: comparable,
    hasT7Data: comparable,
    today: {
      overdueCount: ov.currentCount,
      overdueValue: ov.currentValue,
      rejectedCount: rej.currentCount,
      rejectedValue: rej.currentValue,
      shortGrnCount: sg.currentCount,
      shortGrnDiscrepancy: sg.currentValue,
      noGrn30Count: grn.over30Count,
      noGrn30Value: grn.over30Value,
    },
    wow: {
      overdueValue: ov.deltaPct,
      rejectedValue: rej.deltaPct,
      shortGrnDiscrepancy: sg.deltaPct,
      noGrn30Value: grn.over30DeltaPct,
      discAddedThisWeek: discAdded,
    },
  };
}

export function buildSummaryRows(clients) {
  return (clients || []).map(c => ({
    clientId: c.clientId,
    clientName: c.clientName,
    kamName: c.kamName,
    accountExecutiveName: c.accountExecutiveName,
    activationStatus: c.activationStatus,
    cohort: c.cohort,
    newPlatform: Boolean(c.newPlatform),
    newPlatforms: c.newPlatforms || [],
    wowComparable: isWowComparable(c),
    wowBlockedReason: wowBlockedReason(c),
    hasPriorWeekData: isWowComparable(c),
    allocationStatus: c.allocation?.status ?? 'NONE',
    unassignedExposureValue: c.allocation?.unassignedExposureValue ?? 0,
    assigneeCount: c.allocation?.assigneeCount ?? 0,
    totals: c.metrics?.totals ?? {},
    overdue:  issue(c, 'payment_overdue'),
    rejected: issue(c, 'unbooked_invoice'),
    noGrn:    issue(c, 'grn_missing'),
    shortGrn: issue(c, 'grn_short'),
    responsibleCount: (c.responsibles || []).length,
    closedLastWeek: c.metrics?.totals?.closedLastWeekValue ?? 0,
    error: c.error ?? null,
  }));
}

export function buildResponsibleRows(clients) {
  const rows = [];
  (clients || []).forEach(c => {
    (c.responsibles || []).forEach(r => {
      rows.push({
        ...r,
        clientId: c.clientId,
        clientName: c.clientName,
        kamName: c.kamName,
        activationStatus: c.activationStatus ?? null,
        wowComparable: isWowComparable(c),
        wowBlockedReason: wowBlockedReason(c),
        issueLabel: ISSUE_TYPES[r.issueType]?.label ?? r.issueType,
      });
    });
  });
  return rows;
}

export function buildKamNames(clients) {
  return [...new Set((clients || []).map(c => c.kamName).filter(Boolean))].sort();
}

export const matchesKam = (kamName, selected) =>
  selected.length === 0 || selected.includes(kamName);

// Activation scoping. Trial brands are shown and exported, but never in the
// same file as active ones unless that is asked for explicitly, so a trial
// account can't be swept into a production send by accident.
export const matchesActivation = (activationStatus, selected) =>
  selected.length === 0 || selected.includes(activationStatus);

export const filterCampaignRows = (rows, kamFilter, activationFilter) =>
  (rows || []).filter(r =>
    matchesKam(r.kam_name, kamFilter) &&
    matchesActivation(r.activation_status, activationFilter));

// ── Message builders (ported from dataEngine.js) ─────────────────────────────

function founderMessage(firstName, m) {
  const { overdueValue: ov, overdueCount: oc, rejectedValue: rv, rejectedCount: rc,
          noGrn30Value: ng30v, noGrn30Count: ng30c } = m.today;
  const wowNoGrn = m.wow.noGrn30Value;
  const wowOv = m.wow.overdueValue;
  const wowRej = m.wow.rejectedValue;

  let opening;
  if (rv > 0 && wowRej !== null && parseFloat(wowRej) > 0)
    opening = `⚠️ ${fmtInr(rv)} in Rejected Invoices this week — your finance team needs to act on these.`;
  else if (wowNoGrn !== null && parseFloat(wowNoGrn) > 0)
    opening = `⚠️ No GRN 30+ days is up ${Math.round(parseFloat(wowNoGrn))}% this week — your team needs to act before these age further.`;
  else if (wowOv !== null && parseFloat(wowOv) > 0)
    opening = `⚠️ Overdue payments are up ${Math.round(parseFloat(wowOv))}% this week — follow up now to protect your cashflow.`;
  else if (wowOv !== null && parseFloat(wowOv) < 0)
    opening = `✅ Overdue payments are down ${Math.round(Math.abs(parseFloat(wowOv)))}% this week — your team is making progress.`;
  else
    opening = `⚠️ Here is your weekly ARPFlow summary to track and act on open items.`;

  const bullets = [];
  if (ov > 0 && oc > 0)
    bullets.push(`<li><b>${fmtInr(ov)}</b> overdue across <b>${oc} invoices</b> — finance team can follow up directly via ARPFlow Draft Email.</li>`);
  if (rv > 0 && rc > 0)
    bullets.push(`<li>Resolve <b>${fmtInr(rv)}</b> in Rejected Invoices across <b>${rc} invoice${rc !== 1 ? 's' : ''}</b> — finance team can action these directly in the Rejected Invoices section on ARPFlow.</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li><b>${ng30c} invoices</b> worth <b>${fmtInr(ng30v)}</b> have no GRN for 30+ days — ops team needs to confirm delivery or raise dispute.</li>`);
  if (!bullets.length)
    bullets.push(`<li>All key metrics look healthy this week — keep tracking on ARPFlow.</li>`);

  return `<p>${opening}</p>\n<p>Hi ${firstName}, here's your weekly snapshot —</p>\n<ul>\n${bullets.join('\n')}\n</ul>\n<p>Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

function founderSummaryMessage(firstName, m) {
  const { overdueValue: ov, overdueCount: oc, noGrn30Value: ng30v, noGrn30Count: ng30c,
          shortGrnDiscrepancy: sgd, shortGrnCount: sgc, rejectedValue: rv, rejectedCount: rc } = m.today;

  const bullets = [];
  if (ov > 0 && oc > 0)
    bullets.push(`<li><b>Payment Overdue:</b> ${fmtInr(ov)} across ${oc} invoice${oc !== 1 ? 's' : ''}</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li><b>No GRN 30+ Days:</b> ${fmtInr(ng30v)} across ${ng30c} invoice${ng30c !== 1 ? 's' : ''}</li>`);
  if (sgd > 0 && sgc > 0)
    bullets.push(`<li><b>Short GRN Discrepancy:</b> ${fmtInr(sgd)} across ${sgc} invoice${sgc !== 1 ? 's' : ''}</li>`);
  if (rv > 0 && rc > 0)
    bullets.push(`<li><b>Rejected Invoices:</b> ${fmtInr(rv)} across ${rc} invoice${rc !== 1 ? 's' : ''}</li>`);

  const body = bullets.length
    ? `<ul>\n${bullets.join('\n')}\n</ul>`
    : `<p>All metrics look healthy this week — keep tracking on ARPFlow.</p>`;

  return `<p>Hi ${firstName}, here's your ARPFlow snapshot for this week across all active platforms —</p>\n${body}\n<p>Your team can act on all of these directly on ARPFlow. Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

// Owner-level message — the one thing the snapshot dashboard could not produce,
// because it had no idea who was responsible for what.
export function responsibleMessage(responsible) {
  const label = ISSUE_TYPES[responsible.issueType]?.label ?? responsible.issueType;
  const firstName = (responsible.name || '').split(' ')[0] || 'there';
  const movement = responsibleMovement(responsible);

  const bullets = [
    `<li>You own <b>${fmtInr(responsible.currentValue)}</b> of open <b>${label}</b> — ${movement}.</li>`,
    responsible.pendingValue > 0 && `<li><b>${fmtInr(responsible.pendingValue)}</b> has had no action taken yet.</li>`,
    responsible.disputeValue > 0 && `<li><b>${fmtInr(responsible.disputeValue)}</b> is currently under dispute.</li>`,
    responsible.old90Value > 0 && `<li><b>${fmtInr(responsible.old90Value)}</b> has been open 90+ days.</li>`,
    responsible.closedLastWeek > 0 && `<li>✅ You closed <b>${fmtInr(responsible.closedLastWeek)}</b> in the last 7 days.</li>`,
    responsible.actionAgeingDays !== null && responsible.actionAgeingDays !== undefined
      && responsible.actionAgeingDays > 7
      && `<li>⚠️ Nothing has been actioned here for <b>${responsible.actionAgeingDays} days</b>.</li>`,
    (responsible.topCustomers || []).length > 0
      && `<li>Biggest exposure: ${responsible.topCustomers.slice(0, 3).map(c => `<b>${c.customerName}</b> (${fmtInr(c.amount)})`).join(', ')}.</li>`,
  ].filter(Boolean);

  return `<p>Hi ${firstName}, here's your ARPFlow ownership snapshot —</p>\n<ul>\n${bullets.join('\n')}\n</ul>\n<p>Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

// ── Responsibility message parts ─────────────────────────────────────────────
//
// The HTML `responsibleMessage` above is what the dashboard shows. SheetWA
// needs the opposite shape: one plain-text template per message variant, with
// @column placeholders that the sheet fills in. These helpers pre-render the
// two parts that cannot be templated — the owned line (its issue label and
// movement wording differ per row) and the tail bullets (variable in number) —
// exactly as Founder WoW pre-renders `opening_line`.

export function responsibleMovement(r) {
  if (r.deltaPct === null || r.deltaPct === undefined) {
    return r.wowComparable === false
      ? 'not comparable with last week'
      : 'no prior-week baseline to compare against';
  }
  if (r.deltaPct > 0) return `up ${Math.round(r.deltaPct)}% vs last week`;
  if (r.deltaPct < 0) return `down ${Math.round(Math.abs(r.deltaPct))}% vs last week`;
  return 'flat vs last week';
}

export function responsibleOwnedLine(r) {
  const label = ISSUE_TYPES[r.issueType]?.label ?? r.issueType;
  return `You own ${fmtInr(r.currentValue)} of open ${label} — ${responsibleMovement(r)}.`;
}

// Closed / stale-action / biggest-exposure. Variable in number, so they travel
// as one pre-rendered block rather than three optional template lines.
export function responsibleExtraLines(r) {
  return [
    r.closedLastWeek > 0 && `• ✅ You closed ${fmtInr(r.closedLastWeek)} in the last 7 days.`,
    r.actionAgeingDays !== null && r.actionAgeingDays !== undefined && r.actionAgeingDays > 7
      && `• ⚠️ Nothing has been actioned here for ${r.actionAgeingDays} days.`,
    (r.topCustomers || []).length > 0
      && `• Biggest exposure: ${r.topCustomers.slice(0, 3).map(c => `${c.customerName} (${fmtInr(c.amount)})`).join(', ')}.`,
  ].filter(Boolean).join('\n');
}

// ── Campaign row builder (ported from dataEngine.js buildCampaignRows) ───────

// Every product user at the client, deduped. Responsibles are contacts too and
// are frequently the right recipient, so they are folded in rather than lost.
function contactsOf(client) {
  const seen = new Set();
  const out = [];
  const push = u => {
    if (!u || u.userId == null || seen.has(u.userId)) return;
    seen.add(u.userId);
    out.push(u);
  };
  (client.unallocatedContacts || []).forEach(push);
  (client.responsibles || []).forEach(r => push({
    userId: r.userId,
    firstName: (r.name || '').split(' ')[0],
    lastName: null,
    email: r.email,
    contactNumber: r.contactNumber,
    designation: r.designation,
  }));
  return out;
}

// `clientFilter` decides which clients a campaign covers. It replaced a pair of
// newPlatform booleans: Founder Summary now covers every client, and Founder WoW
// selects on whether a like-for-like comparison exists at all, which is a
// broader question than "did a platform just go live".
function buildCampaignRows(clients, msgFn, eligibleDesigs, clientFilter = () => true) {
  const rows = [];
  (clients || []).forEach(client => {
    const m = toLegacyMetrics(client);
    if (!clientFilter(m)) return;

    const wowDisc = m.wow.discAddedThisWeek || 0;
    const showWoW = m.wowComparable;

    contactsOf(client).forEach(u => {
      if (!eligibleDesigs.has(u.designation)) return;
      rows.push({
        contact_number: u.contactNumber || '',
        first_name: u.firstName || '',
        kam_name: client.kamName || '',
        activation_status: client.activationStatus || '',
        Message: msgFn(u.firstName || 'there', m),
        // Individual KPI columns
        overdue_value:             m.today.overdueValue > 0 ? fmtInr(m.today.overdueValue) : 'Nil',
        overdue_count:             m.today.overdueCount,
        overdue_wow:               showWoW ? fmtPctStr(m.wow.overdueValue) : 'N/A',
        overdue_direction:         showWoW ? fmtPctDir(m.wow.overdueValue) : 'N/A',
        rejected_value:            m.today.rejectedValue > 0 ? fmtInr(m.today.rejectedValue) : 'Nil',
        rejected_count:            m.today.rejectedCount,
        rejected_wow:              showWoW ? fmtPctStr(m.wow.rejectedValue) : 'N/A',
        nogrn30_value:             m.today.noGrn30Value > 0 ? fmtInr(m.today.noGrn30Value) : 'Nil',
        nogrn30_count:             m.today.noGrn30Count,
        nogrn30_wow:               showWoW ? fmtPctStr(m.wow.noGrn30Value) : 'N/A',
        nogrn30_direction:         showWoW ? fmtPctDir(m.wow.noGrn30Value) : 'N/A',
        short_grn_discrepancy:     m.today.shortGrnDiscrepancy > 0 ? fmtInr(m.today.shortGrnDiscrepancy) : 'Nil',
        short_grn_count:           m.today.shortGrnCount,
        new_discrepancy_this_week: wowDisc > 0 ? fmtInr(wowDisc) : 'Nil',
        short_grn_wow:             showWoW ? fmtPctStr(m.wow.shortGrnDiscrepancy) : 'N/A',
        // ref
        client_name:  m.clientName,
        designation:  u.designation,
        new_platform: m.newPlatform ? 'Yes' : 'No',
        wow_comparable: m.wowComparable ? 'Yes' : 'No',
        report_date:  new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      });
    });
  });
  return rows;
}

export function buildLiveCampaigns(clients) {
  // One row per person per issue type they own. Same contact_number / Message
  // lead-in as the campaign sheets, so the SheetWA setup is identical, with the
  // ownership detail behind it. WoW is already null when the client has no
  // like-for-like baseline, so it renders N/A rather than a made-up number.
  // One row per person per issue type they own. Optional amounts read 'Nil'
  // rather than '0' so the variant key can be derived from the sheet values,
  // the same convention the campaign rows use.
  const responsibles = buildResponsibleRows(clients).map(r => ({
    contact_number: r.contactNumber || '',
    first_name: (r.name || '').split(' ')[0],
    kam_name: r.kamName || '',
    Message: responsibleMessage(r),
    owned_line: responsibleOwnedLine(r),
    extra_lines: responsibleExtraLines(r),
    owned_value: fmtInr(r.currentValue),
    owned_wow: fmtPctStr(r.deltaPct),
    owned_direction: fmtPctDir(r.deltaPct),
    pending_value: r.pendingValue > 0 ? fmtInr(r.pendingValue) : 'Nil',
    dispute_value: r.disputeValue > 0 ? fmtInr(r.disputeValue) : 'Nil',
    old_90d_value: r.old90Value > 0 ? fmtInr(r.old90Value) : 'Nil',
    closed_last_week: r.closedLastWeek > 0 ? fmtInr(r.closedLastWeek) : 'Nil',
    action_ageing_days: r.actionAgeingDays ?? '',
    severity: r.status,
    issue_type: r.issueLabel,
    client_name: r.clientName,
    client_id: r.clientId,
    designation: r.designation || '',
    email: r.email || '',
    activation_status: r.activationStatus || '',
    wow_comparable: r.wowComparable ? 'Yes' : 'No',
    report_date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
  }));

  const allocationChase = (clients || [])
    .filter(c => c.allocation?.status !== 'FULL')
    .flatMap(c => (c.unallocatedContacts || []).map(u => ({
      contact_number: u.contactNumber || '',
      first_name: u.firstName || '',
      kam_name: c.kamName || '',
      client_name: c.clientName,
      client_id: c.clientId,
      activation_status: c.activationStatus || '',
      designation: u.designation || '',
      email: u.email || '',
      allocation_status: c.allocation?.status ?? 'NONE',
      unassigned_exposure: fmtInr(c.allocation?.unassignedExposureValue ?? 0),
      unassigned_issue_types: (c.allocation?.unassignedIssueTypes || []).join(', '),
      report_date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    })));

  return {
    // Every client, every product user. There is no founder-only variant any
    // more — restricting the audience by designation dropped the 24 of 51
    // active clients that have no founder user on record.
    founderSummary: buildCampaignRows(clients, founderSummaryMessage, ALL_DESIGS),
    // Same audience, but only clients whose baseline covers the same platforms
    // as this week. Anywhere else the message would be quoting movement that
    // is an artefact of the platform mix changing.
    founderWoW: buildCampaignRows(clients, founderMessage, ALL_DESIGS, m => m.wowComparable),
    responsibles,
    allocationChase,
  };
}

// ── Founder WoW SheetWA export (ported from dataEngine.js) ──────────────────

function _founderOpeningLine(r) {
  if (r.rejected_value !== 'Nil' && r.rejected_wow !== 'N/A' && r.rejected_wow.startsWith('+'))
    return `⚠️ ${r.rejected_value} in Rejected Invoices this week — your finance team needs to act on these.`;
  if (r.nogrn30_wow !== 'N/A' && r.nogrn30_wow.startsWith('+'))
    return `⚠️ No GRN 30+ days is up ${Math.round(parseFloat(r.nogrn30_wow))}% this week — your team needs to act before these age further.`;
  if (r.overdue_wow !== 'N/A' && r.overdue_wow.startsWith('+'))
    return `⚠️ Overdue payments are up ${Math.round(parseFloat(r.overdue_wow))}% this week — follow up now to protect your cashflow.`;
  if (r.overdue_wow !== 'N/A' && r.overdue_wow.startsWith('-'))
    return `✅ Overdue payments are down ${Math.round(Math.abs(parseFloat(r.overdue_wow)))}% this week — your team is making progress.`;
  return `⚠️ Here is your weekly ARPFlow summary to track and act on open items.`;
}

function _variantKey(r) {
  const parts = [];
  if (r.overdue_count > 0 && r.overdue_value !== 'Nil') parts.push('OV');
  if (r.rejected_count > 0 && r.rejected_value !== 'Nil') parts.push('REJ');
  if (r.nogrn30_count > 0 && r.nogrn30_value !== 'Nil') parts.push('NOGRN');
  return parts.join('+') || 'HEALTHY';
}

const _VARIANT_SHEET_NAMES = {
  'OV+REJ+NOGRN': 'OV+REJ+NOGRN',
  'OV+REJ':       'OV+REJ',
  'OV+NOGRN':     'OV+NOGRN',
  'REJ+NOGRN':    'REJ+NOGRN',
  'OV':           'Overdue only',
  'REJ':          'Rejected only',
  'NOGRN':        'No GRN only',
  'HEALTHY':      'Healthy',
};
const _VARIANT_ORDER = ['OV+REJ+NOGRN', 'OV+REJ', 'OV+NOGRN', 'REJ+NOGRN', 'OV', 'REJ', 'NOGRN', 'HEALTHY'];

function _variantCfg(vk) {
  const hasOv    = vk !== 'HEALTHY' && vk.includes('OV');
  const hasRej   = vk !== 'HEALTHY' && vk.includes('REJ');
  const hasNoGrn = vk !== 'HEALTHY' && vk.includes('NOGRN');
  const healthy  = vk === 'HEALTHY';
  const sheetName = _VARIANT_SHEET_NAMES[vk] || vk;

  const metricCols = [
    ...(hasOv    ? ['overdue_value',  'overdue_count']  : []),
    ...(hasRej   ? ['rejected_value', 'rejected_count'] : []),
    ...(hasNoGrn ? ['nogrn30_count',  'nogrn30_value']  : []),
  ];
  const headers = [
    'contact_number', 'first_name', 'kam_name', 'opening_line',
    ...metricCols,
    'client_name', 'designation', 'new_platform', 'report_date',
  ];

  const bullets = [
    hasOv    && `• @overdue_value overdue across @overdue_count invoices — finance team can follow up directly via ARPFlow Draft Email.`,
    hasRej   && `• Resolve @rejected_value in Rejected Invoices across @rejected_count invoices — finance team can action these directly in the Rejected Invoices section on ARPFlow.`,
    hasNoGrn && `• @nogrn30_count invoices worth @nogrn30_value have no GRN for 30+ days — ops team needs to confirm delivery or raise dispute.`,
    healthy  && `• All key metrics look healthy this week — keep tracking on ARPFlow.`,
  ].filter(Boolean);

  const template = [
    `@opening_line`,
    ``,
    `Hi @first_name, here's your weekly snapshot —`,
    ``,
    ...bullets,
    ``,
    `Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.`,
  ].join('\n');

  const metricWidths = [
    ...(hasOv    ? [14, 12] : []),
    ...(hasRej   ? [14, 12] : []),
    ...(hasNoGrn ? [12, 14] : []),
  ];
  const colWidths = [18, 14, 12, 70, ...metricWidths, 22, 18, 9, 11];

  return { sheetName, headers, template, colWidths, varCols: ['opening_line', ...metricCols] };
}

// ── SheetWA variant workbook ─────────────────────────────────────────────────
//
// SheetWA applies ONE message template per sheet. Any campaign whose message
// has optional bullets therefore cannot ship as a single sheet: a template
// written for the fullest message would emit "Nil across 0 invoices" for every
// recipient missing that bullet.
//
// So rows are grouped by which bullets their message actually carries, each
// group becomes a sheet holding only the columns its own template references,
// and a Templates sheet carries the paste-ready text per sheet.
//
// All three message campaigns share this; they differ only in how a row's
// variant is derived and what each variant's template says.
function writeVariantWorkbook({
  rows, campaignName, kamFilter, variantOrder, variantKeyOf, cfgOf, enrich,
}) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  const sorted = [...filtered].sort((a, b) =>
    (a.client_name || '').localeCompare(b.client_name || '')
    || (a.first_name || '').localeCompare(b.first_name || ''));

  const enriched = sorted.map(r => {
    const withExtras = enrich ? enrich(r) : r;
    return { ...withExtras, _variant: variantKeyOf(withExtras) };
  });

  const groups = {};
  enriched.forEach(r => {
    (groups[r._variant] = groups[r._variant] || []).push(r);
  });

  const wb = XLSX.utils.book_new();
  const tmplData = [['Variant Sheet', 'Count', 'Variable Columns', 'Template Message (paste into SheetWA)']];

  variantOrder.forEach(vk => {
    const varRows = groups[vk];
    if (!varRows) return;
    const cfg = cfgOf(vk);
    const wsData = [cfg.headers, ...varRows.map(r => cfg.headers.map(h => r[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = cfg.colWidths.map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, cfg.sheetName);
    tmplData.push([cfg.sheetName, varRows.length, cfg.varCols.join(', '), cfg.template]);
  });

  // No rows means no sheets, and a workbook with none is a file SheetWA
  // rejects — better to write nothing than to hand over a broken upload.
  if (tmplData.length === 1) return;

  const tmplWs = XLSX.utils.aoa_to_sheet(tmplData);
  tmplWs['!cols'] = [{ wch: 18 }, { wch: 8 }, { wch: 50 }, { wch: 100 }];
  XLSX.utils.book_append_sheet(wb, tmplWs, 'Templates');

  const label = kamFilter ? `_${kamFilter}` : '_AllKAMs';
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${campaignName}${label}_${date}.xlsx`);
}

// Every ordering below runs fullest-message first, so the busiest cohort is the
// first sheet in the book rather than buried behind the quiet ones.
function _variantOrder(tags, emptyKey) {
  const combos = [];
  for (let mask = (1 << tags.length) - 1; mask >= 1; mask -= 1) {
    combos.push({
      key: tags.filter((_, i) => mask & (1 << i)).join('+'),
      size: tags.filter((_, i) => mask & (1 << i)).length,
    });
  }
  return [
    ...combos.sort((a, b) => b.size - a.size).map(c => c.key),
    emptyKey,
  ];
}

export function exportFounderWoWExcel(rows, campaignName, kamFilter) {
  return writeVariantWorkbook({
    rows,
    campaignName,
    kamFilter,
    variantOrder: _VARIANT_ORDER,
    variantKeyOf: r => _variantKey(r),
    cfgOf: _variantCfg,
    enrich: r => ({ ...r, opening_line: _founderOpeningLine(r) }),
  });
}

// ── Founder Summary SheetWA export ───────────────────────────────────────────
//
// Four optional bullets rather than Founder WoW's three, and no opening line —
// the summary opens the same way for everyone, so only the metric bullets vary.

function _summaryVariantKey(r) {
  const parts = [];
  if (r.overdue_count > 0 && r.overdue_value !== 'Nil') parts.push('OV');
  if (r.nogrn30_count > 0 && r.nogrn30_value !== 'Nil') parts.push('NOGRN');
  if (r.short_grn_count > 0 && r.short_grn_discrepancy !== 'Nil') parts.push('SG');
  if (r.rejected_count > 0 && r.rejected_value !== 'Nil') parts.push('REJ');
  return parts.join('+') || 'HEALTHY';
}

const _SUMMARY_SHEET_NAMES = {
  OV: 'Overdue only',
  NOGRN: 'No GRN only',
  SG: 'Short GRN only',
  REJ: 'Rejected only',
  HEALTHY: 'Healthy',
};

const _SUMMARY_VARIANT_ORDER = _variantOrder(['OV', 'NOGRN', 'SG', 'REJ'], 'HEALTHY');

function _summaryVariantCfg(vk) {
  const has = tag => vk.split('+').includes(tag);
  const hasOv = has('OV');
  const hasNoGrn = has('NOGRN');
  const hasSg = has('SG');
  const hasRej = has('REJ');
  const healthy = vk === 'HEALTHY';

  // Bullet order matches founderSummaryMessage so the SheetWA send reads the
  // same as the message previewed in the dashboard.
  const metricCols = [
    ...(hasOv ? ['overdue_value', 'overdue_count'] : []),
    ...(hasNoGrn ? ['nogrn30_value', 'nogrn30_count'] : []),
    ...(hasSg ? ['short_grn_discrepancy', 'short_grn_count'] : []),
    ...(hasRej ? ['rejected_value', 'rejected_count'] : []),
  ];

  const headers = [
    'contact_number', 'first_name', 'kam_name',
    ...metricCols,
    'client_name', 'designation', 'activation_status', 'report_date',
  ];

  const bullets = [
    hasOv && `• Payment Overdue: @overdue_value across @overdue_count invoices`,
    hasNoGrn && `• No GRN 30+ Days: @nogrn30_value across @nogrn30_count invoices`,
    hasSg && `• Short GRN Discrepancy: @short_grn_discrepancy across @short_grn_count invoices`,
    hasRej && `• Rejected Invoices: @rejected_value across @rejected_count invoices`,
    healthy && `• All metrics look healthy this week — keep tracking on ARPFlow.`,
  ].filter(Boolean);

  const template = [
    `Hi @first_name, here's your ARPFlow snapshot for this week across all active platforms —`,
    ``,
    ...bullets,
    ``,
    `Your team can act on all of these directly on ARPFlow. Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.`,
  ].join('\n');

  const colWidths = [18, 14, 12, ...metricCols.map(() => 14), 22, 18, 16, 11];

  return {
    sheetName: (_SUMMARY_SHEET_NAMES[vk] || vk).slice(0, 28),
    headers,
    template,
    colWidths,
    varCols: metricCols,
  };
}

export function exportFounderSummaryExcel(rows, campaignName, kamFilter) {
  return writeVariantWorkbook({
    rows,
    campaignName,
    kamFilter,
    variantOrder: _SUMMARY_VARIANT_ORDER,
    variantKeyOf: _summaryVariantKey,
    cfgOf: _summaryVariantCfg,
  });
}

// ── Responsibility Message SheetWA export ────────────────────────────────────
//
// Same mechanism as exportFounderWoWExcel: SheetWA takes ONE message template
// per sheet, so rows are grouped by which optional bullets their message
// carries, and each group becomes a sheet holding only the columns its own
// template references. A Templates sheet lists the text to paste for each.
//
// A single flat sheet cannot work here — a template written for the fullest
// message would emit "Nil has had no action taken yet" for everyone else.
//
// The variant key uses the three exposure-state bullets, mirroring Founder
// WoW's OV/REJ/NOGRN. The tail bullets (closed, stale action, biggest
// exposure) vary in number, so they ride along pre-rendered in `extra_lines`
// and add one further EXTRA dimension so the template never leaves a stray
// blank line when a row has none.

function _respVariantKey(r) {
  const parts = [];
  if (r.pending_value !== 'Nil') parts.push('PEND');
  if (r.dispute_value !== 'Nil') parts.push('DISP');
  if (r.old_90d_value !== 'Nil') parts.push('OLD90');
  if (r.extra_lines) parts.push('EXTRA');
  return parts.join('+') || 'OWNED';
}

const _RESP_SHEET_NAMES = {
  OWNED: 'Owned only',
  PEND: 'No action only',
  DISP: 'Disputed only',
  OLD90: '90+ only',
  EXTRA: 'Activity only',
};

function _respVariantCfg(vk) {
  const has = tag => vk.split('+').includes(tag);
  const hasPend = has('PEND');
  const hasDisp = has('DISP');
  const hasOld90 = has('OLD90');
  const hasExtra = has('EXTRA');

  const metricCols = [
    ...(hasPend ? ['pending_value'] : []),
    ...(hasDisp ? ['dispute_value'] : []),
    ...(hasOld90 ? ['old_90d_value'] : []),
  ];

  const headers = [
    'contact_number', 'first_name', 'kam_name', 'owned_line',
    ...metricCols,
    ...(hasExtra ? ['extra_lines'] : []),
    'issue_type', 'client_name', 'designation', 'activation_status',
    'wow_comparable', 'report_date',
  ];

  const bullets = [
    `• @owned_line`,
    hasPend && `• @pending_value has had no action taken yet.`,
    hasDisp && `• @dispute_value is currently under dispute.`,
    hasOld90 && `• @old_90d_value has been open 90+ days.`,
    hasExtra && `@extra_lines`,
  ].filter(Boolean);

  const template = [
    `Hi @first_name, here's your ARPFlow ownership snapshot —`,
    ``,
    ...bullets,
    ``,
    `Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.`,
  ].join('\n');

  const colWidths = [
    18, 14, 12, 70,
    ...metricCols.map(() => 14),
    ...(hasExtra ? [70] : []),
    14, 22, 18, 16, 14, 11,
  ];

  return {
    sheetName: (_RESP_SHEET_NAMES[vk] || vk).slice(0, 28),
    headers,
    template,
    colWidths,
    varCols: ['owned_line', ...metricCols, ...(hasExtra ? ['extra_lines'] : [])],
  };
}

// Fullest message first, same ordering principle as _VARIANT_ORDER: more
// bullets before fewer, so the busiest cohort is the first sheet in the book.
const _RESP_VARIANT_ORDER = [
  'PEND+DISP+OLD90+EXTRA', 'PEND+DISP+OLD90',
  'PEND+DISP+EXTRA', 'PEND+OLD90+EXTRA', 'DISP+OLD90+EXTRA',
  'PEND+DISP', 'PEND+OLD90', 'DISP+OLD90',
  'PEND+EXTRA', 'DISP+EXTRA', 'OLD90+EXTRA',
  'PEND', 'DISP', 'OLD90', 'EXTRA', 'OWNED',
];

export function exportResponsibleExcel(rows, campaignName, kamFilter) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  const sorted = [...filtered].sort((a, b) =>
    (a.client_name || '').localeCompare(b.client_name || '')
    || (a.first_name || '').localeCompare(b.first_name || ''));

  const groups = {};
  sorted.forEach(r => {
    const vk = _respVariantKey(r);
    (groups[vk] = groups[vk] || []).push(r);
  });

  const wb = XLSX.utils.book_new();
  const tmplData = [['Variant Sheet', 'Count', 'Variable Columns', 'Template Message (paste into SheetWA)']];

  _RESP_VARIANT_ORDER.forEach(vk => {
    const varRows = groups[vk];
    if (!varRows) return;
    const cfg = _respVariantCfg(vk);
    const wsData = [cfg.headers, ...varRows.map(r => cfg.headers.map(h => r[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = cfg.colWidths.map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, cfg.sheetName);
    tmplData.push([cfg.sheetName, varRows.length, cfg.varCols.join(', '), cfg.template]);
  });

  // Nothing to write rather than an empty workbook SheetWA would reject.
  if (tmplData.length === 1) return;

  const tmplWs = XLSX.utils.aoa_to_sheet(tmplData);
  tmplWs['!cols'] = [{ wch: 18 }, { wch: 8 }, { wch: 50 }, { wch: 100 }];
  XLSX.utils.book_append_sheet(wb, tmplWs, 'Templates');

  const label = kamFilter ? `_${kamFilter}` : '_AllKAMs';
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${campaignName}${label}_${date}.xlsx`);
}

// The flat single-sheet writer that served Finance Exec / Ops Exec / Founder
// Summary is gone: every message campaign now writes a SheetWA variant
// workbook. dataEngine.js keeps its own copy for the legacy snapshot dashboard,
// which is untouched.

// Generic sheet writer for the two live-only campaigns (Responsible Owners,
// Allocation Chase), which have no legacy column contract to honour.
export function exportGenericExcel(rows, campaignName, kamFilter) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  if (!filtered.length) return;
  const sorted = [...filtered].sort((a, b) =>
    (a.client_name || '').localeCompare(b.client_name || '')
    || (a.first_name || '').localeCompare(b.first_name || ''));

  const headers = Object.keys(sorted[0]);
  const wsData = [headers, ...sorted.map(r => headers.map(h => r[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = headers.map(h => ({ wch: h === 'Message' ? 80 : Math.max(12, h.length + 4) }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: sorted.length, c: headers.length - 1 } }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (kamFilter || 'All KAMs').slice(0, 28));
  const label = kamFilter ? `_${kamFilter}` : '_AllKAMs';
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${campaignName}${label}_${date}.xlsx`);
}

// Customer.io expects one flat object per recipient. Emitted as JSON so it can
// be POSTed to a campaign trigger directly.
export function exportCustomerIoJson(rows, campaignName, kamFilter) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  const payload = {
    campaign: campaignName,
    generated_at: new Date().toISOString(),
    recipients: filtered.map(r => ({
      email: r.email || null,
      phone: r.contact_number || null,
      first_name: r.first_name,
      client_name: r.client_name,
      client_id: r.client_id ?? null,
      data: Object.fromEntries(
        Object.entries(r).filter(([k]) =>
          !['email', 'contact_number', 'first_name', 'client_name', 'client_id'].includes(k)),
      ),
    })),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${campaignName}_customerio_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
